import { domainInputFields, isDomainInputVisible } from './fieldInputContract.js';
import { recordFieldValue } from './recordFieldValue.js';

/** Keep the recording form with its answers, using the existing Result snapshot. */
export function recordInputSchema(domain) {
  if (!domain?.primary && !domain?.followUp?.length && !domain?.fields?.length) return null;
  return JSON.parse(JSON.stringify({
    primary: domain?.primary || null,
    ...(domain?.followUp ? { followUp: domain.followUp } : {}),
    ...(domain?.fields ? { fields: domain.fields } : {}),
    ...(domain?.fieldType ? { fieldType: domain.fieldType } : {}),
    ...(domain?.maxScore != null ? { maxScore: domain.maxScore } : {}),
    ...(domainInputFields(domain).some(field => field.weight > 0)
      ? { listBlocks: { weighting: domain?.listBlocks?.weighting !== false } } : {}),
  }));
}

export function hasRecordInputSchema(record) {
  const schema = record?.inputSchema;
  return schema != null && typeof schema === 'object' && !Array.isArray(schema)
    && ['primary', 'followUp', 'fields'].some(key => Object.hasOwn(schema, key));
}

/** Current routing/capabilities remain current; saved questions retain their meaning. */
export function recordInputDomain(record, domain) {
  if (!hasRecordInputSchema(record)) return domain;
  const schema = record.inputSchema;
  return { ...domain, primary: schema.primary || null, followUp: schema.followUp,
    fields: schema.fields, fieldType: schema.fieldType, maxScore: schema.maxScore,
    listBlocks: { ...domain?.listBlocks, weighting: schema.listBlocks?.weighting === true } };
}

export function declaredRecordFields(record, domain) {
  const definition = recordInputDomain(record, domain);
  return [definition?.primary, ...(definition?.followUp || []), ...(definition?.fields || [])].filter(Boolean);
}

/** An older/hidden question is not an unanswered question. Legacy forms are unknown. */
export function recordFieldApplies(record, field, domain) {
  if (!hasRecordInputSchema(record)) return true;
  const definition = recordInputDomain(record, domain);
  const fields = domainInputFields(definition);
  const saved = fields.find(candidate => candidate.id === field?.id);
  if (!saved) return false;
  const values = Object.fromEntries(fields.map(candidate => [candidate.id, recordFieldValue(record, candidate.id, definition)]));
  if (record.resultEntry && definition.primary?.id) {
    values[definition.primary.id] = record.resultEntry.kind === 'status' ? null : record.resultEntry.value;
  }
  return isDomainInputVisible(saved, values, fields);
}

/** Only a saved limit can give a historical answer a denominator. */
export function recordFieldMaximum(record, fieldId, domain) {
  const definition = recordInputDomain(record, domain);
  const positive = value => typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
  const explicit = positive(record?.fieldMaximums?.[fieldId]) ?? positive(record?.maxScores?.[fieldId])
    ?? (fieldId === definition?.primary?.id ? positive(record?.maxScore) : null);
  if (explicit != null) return explicit;
  if (!hasRecordInputSchema(record)) return null;
  const field = declaredRecordFields(record, domain).find(candidate => candidate.id === fieldId);
  return positive(field?.maxScore) ?? (fieldId === definition?.primary?.id ? positive(definition?.maxScore) : null);
}
